import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import type { ThematicEntity, EntityType, NewThematicEntity } from '@/types/thematicMaps';
import { ENTITY_TYPES } from '@/types/thematicMaps';

interface EntityTableProps {
  entities: ThematicEntity[];
  loading: boolean;
  onCreateEntity: (data: Omit<NewThematicEntity, 'user_id' | 'map_id'>) => Promise<ThematicEntity | null>;
  onDeleteEntity: (id: string) => Promise<boolean>;
}

const TYPE_LABELS: Record<EntityType, string> = {
  concept: 'Koncept',
  product: 'Produkt',
  method: 'Metoda',
  tool: 'Narzędzie',
  role: 'Rola',
  problem: 'Problem',
  attribute: 'Atrybut',
};

export function EntityTable({ entities, loading, onCreateEntity, onDeleteEntity }: EntityTableProps) {
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<EntityType>('concept');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    await onCreateEntity({
      canonical_name: newName.trim(),
      entity_type: newType,
      aliases: [],
      description: '',
    });
    setNewName('');
    setNewType('concept');
    setAdding(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Usunąć encję "${name}"?`)) {
      await onDeleteEntity(id);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground py-4">Ładowanie encji...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            placeholder="Nazwa encji..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
        </div>
        <Select value={newType} onValueChange={(v) => setNewType(v as EntityType)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleAdd} disabled={adding || !newName.trim()}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Dodaj
        </Button>
      </div>

      {entities.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Brak encji. Użyj przycisku "Wyciągnij encje z AI" powyżej lub dodaj ręcznie.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Canonical Name</TableHead>
              <TableHead className="w-[100px]">Typ</TableHead>
              <TableHead>Aliasy</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {entities.map((entity) => (
              <TableRow key={entity.id}>
                <TableCell className="font-medium">{entity.canonical_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {TYPE_LABELS[entity.entity_type]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {entity.aliases.map((alias, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {alias}
                      </Badge>
                    ))}
                    {entity.aliases.length === 0 && (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(entity.id, entity.canonical_name)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
